// WebRTC-звонки: сигналинг через готовый WS, медиа P2P.
// Сессия НЕ подписывается на topic сама — подпиской управляет UI (ChatView).
import { realtime } from './realtime';
import { randomUUID } from './api/client';
import { applyIceServers, currentIceServers } from './rtcIce';

export interface RtcSignal {
  type: 'call' | 'answer' | 'ice' | 'reject' | 'end' | 'mute-state' | 'call_hold' | 'call_resume' | 'incoming_closed';
  fromUserId?: string;
  callId?: string;
  reason?: string;
  sdp?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit | null;
  video?: boolean; // видеозвонок?
  // Только у 'mute-state' (см. отчёт пользователя "добавить кнопку выключить звук и
  // отображать всем что звук выключен/включен") — track.enabled не даёт собеседнику узнать
  // о смене состояния сам по себе (событие mute/unmute на принимающей стороне ненадёжно
  // в разных браузерах), поэтому шлём явным сигналом рядом с локальным track.enabled.
  kind?: 'audio' | 'video';
  muted?: boolean;
}

// 'disconnected' часто транзитно (смена сети, кратковременная потеря пакетов) — даём этот
// запас времени на самовосстановление/restartIce() прежде чем считать звонок оборванным.
const ICE_RESTART_DELAY_MS = 6000;
export const OUTGOING_RING_TIMEOUT_MS = 180_000;
export const rtcTopic = (chatId: string) => `/topic/chats/${chatId}/rtc`;
export const rtcApp = (chatId: string) => `/app/rtc/${chatId}`;

export interface RtcHandlers {
  onRemoteStream: (stream: MediaStream) => void;
  onRemoteEnd: () => void;
  onReject: () => void;
  onConnected: () => void;
  /** Ответ получен: UI прекращает гудки до завершения ICE-соединения. */
  onAnswer?: () => void;
  /** Собеседник включил/выключил микрофон или камеру (см. RtcSignal.kind/muted). */
  onRemoteMuteState?: (kind: 'audio' | 'video', muted: boolean) => void;
  onRemoteHold?: (held: boolean) => void;
}

export class RtcSession {
  readonly chatId: string;
  readonly meId: string;
  pc: RTCPeerConnection;
  localStream?: MediaStream;
  private handlers: RtcHandlers;
  private destroyed = false;
  private incoming = false;
  private localHeld = false;
  private micMuted = false;
  private cameraMuted = false;
  private callId: string | null = null;
  private outgoingDeadlineAt: number | null = null;
  private outgoingTimer?: ReturnType<typeof setTimeout>;
  private remoteDescSet = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private facingMode: 'user' | 'environment' = 'user';
  private switchingCamera = false;
  private disconnectTimer?: ReturnType<typeof setTimeout>;

  constructor(chatId: string, meId: string, handlers: RtcHandlers) {
    this.chatId = chatId;
    this.meId = meId;
    this.handlers = handlers;
    this.pc = new RTCPeerConnection({ iceServers: currentIceServers() });
    this.pc.onicecandidate = (e) => this.signal({ type: 'ice', candidate: e.candidate });
    this.pc.ontrack = (e) => this.handlers.onRemoteStream(e.streams[0]);
    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      if (state === 'connected') {
        this.clearDisconnectTimer();
        this.clearOutgoingTimeout();
        this.incoming = false;
        this.handlers.onConnected();
      } else if (state === 'disconnected') {
        // Не убиваем звонок сразу — даём ICE шанс переподключиться самостоятельно, а если за
        // ICE_RESTART_DELAY_MS не помогло — пробуем restartIce() (пересоздаст ICE-кандидаты).
        // Настоящий обрыв обнаруживаем по переходу в 'failed', который браузер выставит сам,
        // если восстановиться не удалось.
        this.clearDisconnectTimer();
        this.disconnectTimer = setTimeout(() => {
          if (this.destroyed || this.pc.connectionState !== 'disconnected') return;
          try { this.pc.restartIce(); } catch { /* не поддерживается или уже закрыт */ }
        }, ICE_RESTART_DELAY_MS);
      } else if (state === 'failed') {
        this.clearDisconnectTimer();
        this.handlers.onRemoteEnd();
        this.destroy();
      }
    };
  }

  private clearDisconnectTimer() {
    if (this.disconnectTimer != null) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = undefined;
    }
  }

  private startOutgoingTimeout(deadlineAt?: number) {
    if (this.incoming || !this.callId) return;
    this.outgoingDeadlineAt = deadlineAt ?? Date.now() + OUTGOING_RING_TIMEOUT_MS;
    this.checkOutgoingTimeout();
  }

  private checkOutgoingTimeout() {
    const deadline = this.outgoingDeadlineAt;
    if (this.destroyed || this.incoming || deadline == null) return;
    if (Date.now() >= deadline) {
      this.clearOutgoingTimeout();
      this.signal({ type: 'end' });
      this.handlers.onRemoteEnd();
      this.destroy();
      return;
    }
    if (this.outgoingTimer != null) clearTimeout(this.outgoingTimer);
    this.outgoingTimer = setTimeout(() => {
      this.outgoingTimer = undefined;
      this.checkOutgoingTimeout();
    }, deadline - Date.now());
  }

  private clearOutgoingTimeout() {
    if (this.outgoingTimer != null) clearTimeout(this.outgoingTimer);
    this.outgoingTimer = undefined;
    this.outgoingDeadlineAt = null;
  }

  get isDestroyed(): boolean {
    return this.destroyed;
  }

  async startWithMedia(audio: boolean, video: boolean): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio,
      video: video ? { facingMode: this.facingMode } : false,
    });
    await this.startWithStream(stream);
  }

  async startWithStream(stream: MediaStream, audioEnabled = true, videoEnabled = true): Promise<void> {
    if (this.destroyed) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.localStream = stream;
    this.micMuted = !audioEnabled;
    this.cameraMuted = !videoEnabled;
    stream.getAudioTracks().forEach((track) => { track.enabled = audioEnabled && !this.localHeld; });
    stream.getVideoTracks().forEach((track) => { track.enabled = videoEnabled && !this.localHeld; });
    try {
      stream.getTracks().forEach((track) => this.pc.addTrack(track, stream));
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      this.localStream = undefined;
      throw error;
    }
  }

  /** Переключить фронтальную/тыловую камеру прямо во время звонка (перевыставляет track в pc). */
  async switchCamera(): Promise<void> {
    if (!this.localStream || this.switchingCamera) return;
    const oldTrack = this.localStream.getVideoTracks()[0];
    if (!oldTrack) return;
    this.switchingCamera = true;
    let newTrack: MediaStreamTrack | undefined;
    try {
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      const newStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: this.facingMode } });
      newTrack = newStream.getVideoTracks()[0];
      newTrack.enabled = !this.cameraMuted && !this.localHeld; // сохраняем mute/hold после смены камеры
      const sender = this.pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(newTrack);
      oldTrack.stop();
      this.localStream.removeTrack(oldTrack);
      this.localStream.addTrack(newTrack); // тот же объект MediaStream — <video> подхватит сам
    } catch {
      newTrack?.stop(); // трек получен, но в соединение не встал — не оставлять камеру захваченной
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user'; // откат — камера не переключилась
    } finally {
      this.switchingCamera = false;
    }
  }

  /** Включить/выключить микрофон — трек остаётся в соединении (без renegotiation), просто
   *  перестаёт передавать звук; собеседник узнаёт об этом по сигналу, не только по факту
   *  тишины (см. RtcSignal.kind/muted). Возвращает новое состояние (true = выключен). */
  toggleMic(): boolean {
    const track = this.localStream?.getAudioTracks()[0];
    if (!track) return false;
    this.micMuted = !this.micMuted;
    track.enabled = !this.micMuted && !this.localHeld;
    this.signal({ type: 'mute-state', kind: 'audio', muted: this.micMuted });
    return this.micMuted;
  }

  /** Включить/выключить камеру — тот же приём, что и toggleMic, для видео-дорожки. */
  toggleCamera(): boolean {
    const track = this.localStream?.getVideoTracks()[0];
    if (!track) return false;
    this.cameraMuted = !this.cameraMuted;
    track.enabled = !this.cameraMuted && !this.localHeld;
    this.signal({ type: 'mute-state', kind: 'video', muted: this.cameraMuted });
    return this.cameraMuted;
  }

  setHeld(held: boolean): void {
    if (this.destroyed || this.localHeld === held) return;
    this.localHeld = held;
    this.localStream?.getAudioTracks().forEach((track) => { track.enabled = !held && !this.micMuted; });
    this.localStream?.getVideoTracks().forEach((track) => { track.enabled = !held && !this.cameraMuted; });
    this.signal({ type: held ? 'call_hold' : 'call_resume' });
  }

  announceHoldState(): void {
    if (this.localHeld && !this.destroyed) this.signal({ type: 'call_hold' });
  }

  onSignalingReconnected(): void {
    this.checkOutgoingTimeout();
  }

  get isHeld(): boolean { return this.localHeld; }

  async call(video: boolean, deadlineAt?: number) {
    await applyIceServers(this.pc); // ждём TURN не дольше ICE_WAIT_MS
    if (this.destroyed) return;
    this.callId = randomUUID();
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    if (this.destroyed) return;
    this.signal({ type: 'call', sdp: offer, video });
    this.startOutgoingTimeout(deadlineAt);
  }

  async answer() {
    if (this.destroyed) return;
    await applyIceServers(this.pc);
    if (this.destroyed) return;
    const answer = await this.pc.createAnswer();
    if (this.destroyed) return;
    await this.pc.setLocalDescription(answer);
    if (this.destroyed) return;
    this.incoming = false;
    this.signal({ type: 'answer', sdp: answer });
  }

  announceInitialMuteState(): void {
    if (this.micMuted) this.signal({ type: 'mute-state', kind: 'audio', muted: true });
    if (this.cameraMuted) this.signal({ type: 'mute-state', kind: 'video', muted: true });
  }

  async onSignal(sig: RtcSignal) {
    if (this.destroyed) return;
    if (sig.type === 'incoming_closed') {
      if (!this.incoming || sig.fromUserId !== this.meId || !this.callId || sig.callId !== this.callId) return;
      if (sig.reason === 'declined' || sig.reason === 'busy') this.handlers.onReject();
      else this.handlers.onRemoteEnd();
      this.destroy();
      return;
    }
    if (this.callId && sig.callId && sig.callId !== this.callId) return;
    if (sig.fromUserId === this.meId) {
      // Отмена с другого устройства того же аккаунта приходит как эхо собственного `end`.
      // Закрываем только исходящий звонок с совпадающим callId; локальное эхо отсечено destroy().
      if (!this.incoming && sig.type === 'end' && sig.callId === this.callId) {
        this.handlers.onRemoteEnd();
        this.destroy();
      }
      return;
    }
    if (sig.type === 'call' && sig.sdp) {
      this.incoming = true;
      this.clearOutgoingTimeout();
      this.callId = sig.callId ?? null;
    }
    if (sig.type === 'answer' && sig.sdp && !this.incoming) {
      this.clearOutgoingTimeout();
      this.handlers.onAnswer?.();
    }
    try {
      if ((sig.type === 'call' || sig.type === 'answer') && sig.sdp) {
        await this.pc.setRemoteDescription(sig.sdp);
        this.remoteDescSet = true;
        // Применяем накопленные ICE-candidates, пришедшие до remote description
        for (const c of this.pendingCandidates) {
          try { await this.pc.addIceCandidate(c); } catch { /* битый кандидат */ }
        }
        this.pendingCandidates = [];
      } else if (sig.type === 'ice' && sig.candidate) {
        if (this.remoteDescSet) {
          await this.pc.addIceCandidate(sig.candidate);
        } else {
          this.pendingCandidates.push(sig.candidate); // ждём remote description
        }
      } else if (sig.type === 'reject') {
        this.handlers.onReject();
        this.destroy();
      } else if (sig.type === 'end') {
        this.handlers.onRemoteEnd();
        this.destroy();
      } else if (sig.type === 'mute-state' && sig.kind && sig.muted != null) {
        this.handlers.onRemoteMuteState?.(sig.kind, sig.muted);
      } else if (sig.type === 'call_hold' || sig.type === 'call_resume') {
        this.handlers.onRemoteHold?.(sig.type === 'call_hold');
      }
    } catch (e) {
      // Транзитная ошибка (например битый candidate) не должна убивать звонок
      console.warn('[rtc] signal error', e);
    }
  }

  reject() {
    this.signal({ type: 'reject' });
    this.destroy();
  }

  hangup(notify = true) {
    if (notify) this.signal({ type: 'end' });
    this.destroy();
  }

  private signal(body: RtcSignal) {
    realtime.publish(rtcApp(this.chatId), this.callId ? { ...body, callId: this.callId } : body);
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearDisconnectTimer();
    this.clearOutgoingTimeout();
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.pc.close();
  }
}
